import { Fragment, useEffect } from 'react';
import { useParams, useHistory } from 'react-router';
import { Redirect } from 'react-router-dom';
import {
  BREAKPOINTS_WIDTHS,
  INTERCOM_CONFIG,
  useWindowSize,
} from '@belong/common';
import { SetupFlowStepsLayout } from 'accounts/components/setup-flow-steps-layout/setup-flow-steps-layout';
import { SetupFlowStepProvider } from 'accounts/contexts/setup-flow-step';
import { useCheckPropertyIdParam } from 'accounts/hooks/homeowners/use-check-property-id-param';
import { useMultiUnit } from 'accounts/hooks/homeowners/use-mult-unit';
import { useSelectedUnit } from 'accounts/hooks/homeowners/use-selected-unit';
import { useSetupFlowSections } from 'accounts/hooks/homeowners/use-setup-flow-sections';
import { useSetupFlowSteps } from 'accounts/hooks/homeowners/use-setup-flow-steps';
import { useUnits } from 'accounts/hooks/homeowners/use-units';
import { useUserIsLogged } from 'accounts/hooks/homeowners/use-user-is-logged';
import { ChatWidget } from 'components/ChatWidget/ChatWidget';
import RouterHeaderNavLink from 'components/HeaderMain/RouterHeaderNavLink/RouterHeaderNavLink';
import { ContactUs } from 'containers/ContactUs/ContactUs';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { FlowStatus } from 'models/enums';
export function HomeownerSetupFlowSteps() {
  const { propertyId, unitId, stepId } = useParams<any>();
  const history = useHistory();

  const { units } = useUnits();
  const { selectedUnit } = useSelectedUnit();
  const { isMultiUnit } = useMultiUnit();
  const { width } = useWindowSize();
  const isMobile = width <= BREAKPOINTS_WIDTHS.MD;
  const homeownersStepsProps = useSetupFlowSteps();
  const { isEnabled: isAccountRedesignEnabled } =
    useFeatureFlag('account-redesign');

  const saveAndExitLink = isAccountRedesignEnabled
    ? `/redirect/flows/homeowner/post-auth`
    : `/accounts/portfolio/properties/${propertyId}`;

  const {
    loading,
    isValidUrl,
    currentStep,
    currentStepFormData,
    allStepsByGate,
    properties,
    selectedProperty,
    goNextStep,
  } = homeownersStepsProps;

  const {
    propertySection,
    accountSection,
    isFlowIncomplete,
    isPricingImprovementReady,
  } = useSetupFlowSections({
    properties,
    allStepsByGate,
    units,
  });

  useCheckPropertyIdParam();
  useUserIsLogged();

  useEffect(() => {
    if (currentStep) {
      if (currentStep.isHidden) {
        goNextStep();
      }
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep?.flowStatus === FlowStatus.Completed) {
      history.push(`/accounts/portfolio/properties/${propertyId}/setup-flow`);
    }
  }, [currentStep, history, propertyId]);

  const isLoading = loading || (stepId && !currentStepFormData);
  const { schedule, isEnabled } = INTERCOM_CONFIG.setupFlow;

  if (!(loading || (stepId && !currentStepFormData))) {
    if (!isValidUrl()) {
      return <Redirect to={`/accounts/portfolio/properties/${propertyId}`} />;
    }
  }

  if (properties) {
    const unit = selectedProperty?.units.find(
      (currentUnit: any) => currentUnit.basicInfo.unitId === unitId,
    );

    if (!selectedProperty || (unitId?.toLowerCase() !== 'na' && !unit)) {
      return <Redirect to="/accounts" />;
    }
  }

  return (
    <Fragment>
      <SetupFlowStepProvider
        {...homeownersStepsProps}
        units={units}
        selectedUnit={selectedUnit}
        propertySection={propertySection}
        accountSection={accountSection}
        isMultiUnit={isMultiUnit}
      >
        {/* NOTE: `schedule`, `containerWidth`, `paddingRight` and `showOnNavbar`
            are no longer props of `ChatWidget`, which neither destructures nor
            spreads them — so they have been inert. Removing them also strips the
            `schedule`/`isMobile` bindings feeding them, which is a cleanup rather
            than a typecheck fix; see §5 of the followups doc. */}
        <ChatWidget
          // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
          schedule={schedule}
          shouldShow={
            isFlowIncomplete &&
            !isPricingImprovementReady &&
            !accountSection.isCompleted
          }
          isEnabled={isEnabled}
          containerWidth="w-container-xl"
          paddingRight="pr-2xl"
          showOnNavbar={isMobile}
        />
        <SetupFlowStepsLayout
          loading={isLoading}
          navigationComponents={[
            // `ContactUs` reads only `className`; `disableChat` was left over
            // from an older signature and had no effect.
            <ContactUs key="ContactUs" />,
            <RouterHeaderNavLink
              key="save"
              label="SAVE & EXIT"
              to={saveAndExitLink}
            />,
          ]}
        />
      </SetupFlowStepProvider>
    </Fragment>
  );
}
